import {
  corridorCellKey,
  OPERATIONAL_CORRIDOR_CELLS,
  providerDeclaresCorridor,
  type CorridorIdentity,
} from "../constants/paymentRoutes.constants";
import { PaymentDirection } from "../types/paymentFees.types";
import type {
  CorridorRouteCell,
  CorridorRouteStatus,
  ProviderRailRoute,
  ProviderRouteKpis,
} from "../types/paymentRoutes.types";

/**
 * Turns the flat route list into the page's real subject: one row per corridor **and direction**, whether or
 * not a route exists for it.
 *
 * Rows, not routes, because the absences are the point. A corridor with no route refuses every payin and
 * payout with `400 no_route`, and an absence rendered as a missing row is invisible — so the operational
 * catalog is enumerated first and the routes are matched onto it.
 */

/** Route of each corridor cell. At most one can exist per corridor (partial-unique index). */
function indexRoutesByCorridor(routes: ProviderRailRoute[]): Map<string, ProviderRailRoute> {
  const byCorridor = new Map<string, ProviderRailRoute>();
  for (const route of routes) {
    const key = corridorCellKey(route);
    const current = byCorridor.get(key);
    // The index guarantees one non-deleted row per corridor, so this only ever fires if the invariant broke
    // server-side. Prefer the active row: it is the one the selector would resolve.
    if (!current || (!current.is_active && route.is_active)) byCorridor.set(key, route);
  }
  return byCorridor;
}

function statusOf(route: ProviderRailRoute | null, corridor: CorridorIdentity): CorridorRouteStatus {
  if (!route) return "MISSING";
  if (!route.is_active) return "BLOCKED";
  if (!providerDeclaresCorridor(route.provider_code, corridor)) return "UNSUPPORTED";
  return "ROUTED";
}

function cellFor(
  corridor: CorridorIdentity,
  route: ProviderRailRoute | null,
  activeCountByProvider: Map<string, number>,
  offCatalog: boolean,
): CorridorRouteCell {
  return {
    key: corridorCellKey(corridor),
    country: corridor.country,
    rail: corridor.rail,
    currency: corridor.currency,
    direction: corridor.direction,
    status: statusOf(route, corridor),
    route,
    providerCorridorCount: route ? (activeCountByProvider.get(route.provider_code) ?? 0) : 0,
    providerDeclaresCorridor: route ? providerDeclaresCorridor(route.provider_code, corridor) : false,
    offCatalog,
  };
}

const DIRECTION_ORDER: Record<string, number> = {
  [PaymentDirection.PAYIN]: 0,
  [PaymentDirection.PAYOUT]: 1,
};

/**
 * The operational cells in catalog order, followed by any route pointing at a corridor outside that catalog.
 *
 * Off-catalog rows are appended rather than dropped: `provider_rail_routes` constrains neither `country` nor
 * `currency` beyond a length, so such a row is real configuration that no wizard can reach — hiding it would
 * make a dead corridor look like it does not exist.
 */
export function buildCorridorCells(routes: ProviderRailRoute[]): CorridorRouteCell[] {
  const byCorridor = indexRoutesByCorridor(routes);

  const activeCountByProvider = new Map<string, number>();
  for (const route of routes) {
    if (!route.is_active) continue;
    activeCountByProvider.set(route.provider_code, (activeCountByProvider.get(route.provider_code) ?? 0) + 1);
  }

  const operational = OPERATIONAL_CORRIDOR_CELLS.map((corridor) =>
    cellFor(corridor, byCorridor.get(corridorCellKey(corridor)) ?? null, activeCountByProvider, false),
  );

  const catalogKeys = new Set(operational.map((cell) => cell.key));
  const offCatalog = routes
    .filter((route) => !catalogKeys.has(corridorCellKey(route)))
    .map((route) => cellFor(route, route, activeCountByProvider, true))
    .sort(
      (a, b) =>
        a.country.localeCompare(b.country) ||
        a.rail.localeCompare(b.rail) ||
        a.currency.localeCompare(b.currency) ||
        DIRECTION_ORDER[a.direction] - DIRECTION_ORDER[b.direction],
    );

  return [...operational, ...offCatalog];
}

/**
 * `routed` / `totalCorridors` cover the operational catalog only — that ratio is the answer to "can the
 * product operate everywhere it offers". `blocked` and `unsupported` count every cell, off-catalog included:
 * both are misconfigurations that cost real money wherever they sit.
 */
export function buildRouteKpis(cells: CorridorRouteCell[]): ProviderRouteKpis {
  const operational = cells.filter((cell) => !cell.offCatalog);
  return {
    routed: operational.filter((cell) => cell.status === "ROUTED").length,
    totalCorridors: operational.length,
    missing: operational.filter((cell) => cell.status === "MISSING").length,
    blocked: cells.filter((cell) => cell.status === "BLOCKED").length,
    unsupported: cells.filter((cell) => cell.status === "UNSUPPORTED").length,
  };
}

/** Corridors whose cell currently has no usable route — the worklist the KPI counts. */
export function unroutedCells(cells: CorridorRouteCell[]): CorridorRouteCell[] {
  return cells.filter((cell) => cell.status === "MISSING" || cell.status === "BLOCKED");
}

/**
 * Active routes per provider code — the only provider-neutral way to describe a provider on screen ("routes N
 * corridors"). Inactive rows are excluded: they route nothing.
 */
export function activeRouteCountByProvider(routes: ProviderRailRoute[]): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const route of routes) {
    if (!route.is_active) continue;
    counts[route.provider_code] = (counts[route.provider_code] ?? 0) + 1;
  }
  return counts;
}
